import React, { useCallback, useEffect, useState } from 'react';
import appointmentService from '../../../../services/appointment.service';
import Pagination from '../../../common/Pagination';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import { confirmDelete, showError, showSuccess } from '../../../../utils/ecxSwal';
import { ClinicInfoPanel } from './info/clinicInfoParts';
import ClinicSettingsSectionHeader from './subparts/ClinicSettingsSectionHeader';
import ClinicSettingsEmpty from './subparts/ClinicSettingsEmpty';
import ClinicSettingsCancelButton from './subparts/ClinicSettingsCancelButton';
import ClinicSettingsFormFooter from './subparts/ClinicSettingsFormFooter';
import ClinicSettingsListSkeleton from './subparts/ClinicSettingsListSkeleton';

const EMPTY_NOTE = {
    type: '',
    minutes: 15,
    description: '',
    notes: '',
};

const PAGE_SIZE = 10;

function AppointmentNotes() {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [addingNew, setAddingNew] = useState(false);
    const [form, setForm] = useState(EMPTY_NOTE);
    const [pageNo, setPageNo] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const loadNotes = useCallback(async () => {
        if (!clinicId) {
            return;
        }

        try {
            setLoading(true);
            const res = await appointmentService.getAppointmentNotes(clinicId, pageNo, PAGE_SIZE);
            const data = res.data;

            setNotes(data.content || []);
            setTotalPages(data.totalPages || 0);
            setTotalElements(data.totalElements || 0);
        } catch (error) {
            console.error(error);
            showError({ title: 'Load Failed', text: 'Failed to load appointment notes.' });
            setNotes([]);
        } finally {
            setLoading(false);
        }
    }, [clinicId, pageNo]);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    const handleFormChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleCancel = () => {
        setAddingNew(false);
        setForm(EMPTY_NOTE);
    };

    const handleCreateNote = async (event) => {
        event.preventDefault();

        if (!form.type.trim()) {
            showError({ title: 'Validation', text: 'Note type is required.' });
            return;
        }

        try {
            setSaving(true);
            await appointmentService.saveAppointmentNotes(clinicId, {
                type: form.type.trim(),
                minutes: Number(form.minutes) || 0,
                description: form.description?.trim() || '',
                notes: form.notes?.trim() || '',
            });
            setForm(EMPTY_NOTE);
            setAddingNew(false);
            setPageNo(0);
            showSuccess({ title: 'Note Created', text: 'Appointment note was added successfully.' });

            const res = await appointmentService.getAppointmentNotes(clinicId, 0, PAGE_SIZE);
            const data = res.data;
            setNotes(data.content || []);
            setTotalPages(data.totalPages || 0);
            setTotalElements(data.totalElements || 0);
        } catch (error) {
            console.error(error);
            showError({ title: 'Create Failed', text: 'Failed to create appointment note.' });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteNote = async (note) => {
        const result = await confirmDelete({
            title: 'Delete Appointment Note?',
            itemName: note.type || 'this appointment note',
            description: 'This note will be permanently removed.',
            onConfirm: () => appointmentService.deleteAppointmentNotes(clinicId, note.id),
        });

        if (result.isConfirmed) {
            showSuccess({ title: 'Deleted', text: 'Appointment note removed.' });
            loadNotes();
        }
    };

    return (
        <>
            <ClinicSettingsSectionHeader
                title="Appointment Notes"
                icon="bi-journal-text"
                subtitle="Manage reusable appointment note templates for your clinic"
                actions={
                    !loading ? (
                        addingNew ? (
                            <ClinicSettingsCancelButton onClick={handleCancel} disabled={saving} />
                        ) : (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-primary rounded-pill"
                                onClick={() => setAddingNew(true)}
                            >
                                <i className="bi bi-plus-circle me-1" />
                                Add Note
                            </button>
                        )
                    ) : null
                }
            />

            {loading ? (
                <ClinicInfoPanel title="Notes" icon="bi-journal-text">
                    <ClinicSettingsListSkeleton rows={4} />
                </ClinicInfoPanel>
            ) : (
                <ClinicInfoPanel
                    title="Notes"
                    icon="bi-journal-text"
                    actions={addingNew ? <ClinicSettingsCancelButton onClick={handleCancel} disabled={saving} /> : null}
                >
                    {addingNew && (
                        <form className="clinic-settings-appointment-notes-form" onSubmit={handleCreateNote}>
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label small fw-semibold text-muted">Type</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={form.type}
                                        onChange={(e) => handleFormChange('type', e.target.value)}
                                        placeholder="e.g. Follow-up"
                                        maxLength={50}
                                        required
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small fw-semibold text-muted">Minutes</label>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        value={form.minutes}
                                        min={1}
                                        onChange={(e) => handleFormChange('minutes', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="col-md-7">
                                    <label className="form-label small fw-semibold text-muted">Description</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={form.description}
                                        onChange={(e) => handleFormChange('description', e.target.value)}
                                        placeholder="Short description"
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label small fw-semibold text-muted">Notes</label>
                                    <textarea
                                        className="form-control form-control-sm"
                                        rows={3}
                                        value={form.notes}
                                        onChange={(e) => handleFormChange('notes', e.target.value)}
                                        placeholder="Additional note content"
                                    />
                                </div>
                            </div>
                            <ClinicSettingsFormFooter isSaving={saving} submitLabel="Save Note" />
                        </form>
                    )}

                    {notes.length === 0 && !addingNew ? (
                        <ClinicSettingsEmpty
                            icon="bi-journal-x"
                            title="No appointment notes yet"
                            description="Create reusable note templates for common appointment types and durations."
                            actionLabel="Add First Note"
                            onAction={() => setAddingNew(true)}
                        />
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0 clinic-settings-appointment-notes-table">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Minutes</th>
                                            <th>Description</th>
                                            <th>Notes</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {notes.map((note) => (
                                            <tr key={note.id}>
                                                <td className="fw-semibold">{note.type}</td>
                                                <td>{note.minutes}</td>
                                                <td>{note.description || '—'}</td>
                                                <td className="text-muted small">{note.notes || '—'}</td>
                                                <td className="text-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger rounded-circle"
                                                        onClick={() => handleDeleteNote(note)}
                                                        title="Delete note"
                                                    >
                                                        <i className="bi bi-trash" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div className="clinic-settings-appointment-notes-pagination">
                                    <span className="text-muted small">
                                        {(totalElements || 0).toLocaleString()} note
                                        {totalElements === 1 ? '' : 's'}
                                    </span>
                                    <Pagination
                                        currentPage={pageNo}
                                        totalPages={totalPages}
                                        onPageChange={setPageNo}
                                        totalElements={totalElements}
                                        pageSize={PAGE_SIZE}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </ClinicInfoPanel>
            )}
        </>
    );
}

export default AppointmentNotes;
